import React, { useState, useRef } from 'react';
import { useAccessToken } from '../../auth/hooks';
import { getAuthConfig } from '../../config/auth';
import type {
  UploadSummary,
  UploadError,
  UploadValidationFailure,
} from '../../api/dastarkhan';
import { isRowError } from '../../api/dastarkhan';
import {
  Button,
  Card,
  Table,
  Stack,
  Flex,
  useTheme,
} from '@fti/design-system';

interface BulkUploadPanelProps {
  projectId: string;
  onUploadComplete: () => void;
  onClose: () => void;
}

function resolveApiUrl(): string {
  let apiUrl = getAuthConfig().apiUrl;
  if (import.meta.env.VITE_ENVIRONMENT === 'dev') {
    try {
      const stored = localStorage.getItem('vibe-dev-mode-config');
      if (stored && JSON.parse(stored).apiTarget === 'local') apiUrl = 'http://localhost:8000';
    } catch { /* ignore */ }
  }
  return apiUrl;
}

function errorToRow(err: UploadError): Record<string, unknown> {
  if (isRowError(err)) {
    return {
      sheet: err.sheet,
      row: String(err.row),
      field: err.field,
      reason: err.reason,
    };
  }
  return {
    sheet: err.sheet ?? err.scope,
    row: '—',
    field: '—',
    reason: err.reason,
  };
}

export const BulkUploadPanel: React.FC<BulkUploadPanelProps> = ({ projectId, onUploadComplete, onClose }) => {
  const { theme } = useTheme();
  const token = useAccessToken();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState<UploadSummary | null>(null);
  const [validationErrors, setValidationErrors] = useState<UploadError[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    setSummary(null);
    setValidationErrors(null);
    setError(null);
  };

  const handleUpload = async () => {
    if (!selectedFile || !token) return;
    setUploading(true);
    setError(null);
    setSummary(null);
    setValidationErrors(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await fetch(`${resolveApiUrl()}/dastarkhan/projects/${projectId}/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (response.status === 422) {
        const body = await response.json().catch(() => null) as UploadValidationFailure | null;
        if (body?.errors?.length) {
          setValidationErrors(body.errors);
        } else {
          setError('Upload rejected (422) — no error details returned');
        }
        return;
      }

      if (!response.ok) {
        const errData = await response.json().catch(() => null);
        throw new Error(errData?.detail || `Upload failed (${response.status})`);
      }

      const result: UploadSummary = await response.json();
      setSummary(result);
      if (result.nodes_created > 0 || result.nodes_updated > 0 || result.edges_created > 0 || result.edges_updated > 0) {
        onUploadComplete();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!token) return;
    setExporting(true);
    try {
      const response = await fetch(`${resolveApiUrl()}/dastarkhan/projects/${projectId}/export-excel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error(`Export failed (${response.status})`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'graph-export.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { /* handled */ }
    finally { setExporting(false); }
  };

  const errorColumns = [
    { key: 'sheet', header: 'Sheet' },
    { key: 'row', header: 'Row' },
    { key: 'field', header: 'Field' },
    { key: 'reason', header: 'Reason' },
  ];

  return (
    <Card style={{ borderRadius: 0, borderBottom: `1px solid ${theme.colors.border.default}` }}>
      <Card.Body>
        <Stack gap="md">
          <Flex justify="between" align="center">
            <h4 style={{ margin: 0, fontSize: '0.95rem', color: theme.colors.text.primary }}>Bulk Upload</h4>
            <Flex gap="xs">
              <Button variant="outline" size="sm" onClick={handleExportExcel} disabled={exporting}>
                {exporting ? 'Exporting...' : 'Export to Excel'}
              </Button>
              <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
            </Flex>
          </Flex>

          <Flex gap="md" align="end">
            <div>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 500, marginBottom: '4px', color: theme.colors.text.primary }}>
                Excel File (.xlsx)
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx"
                onChange={handleFileChange}
                style={{ fontSize: '0.85rem' }}
              />
            </div>
            <Button
              variant="solid"
              color="primary"
              size="sm"
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
          </Flex>

          {error && (
            <div style={{ color: theme.colors.semantic.error.default, fontSize: '0.85rem' }}>
              {error}
            </div>
          )}

          {validationErrors && (
            <Stack gap="sm">
              <div style={{ color: theme.colors.semantic.error.default, fontSize: '0.85rem', fontWeight: 600 }}>
                Upload rejected — {validationErrors.length} error{validationErrors.length === 1 ? '' : 's'} found. Fix the workbook and re-upload.
              </div>
              <Table
                data={validationErrors.map(errorToRow)}
                columns={errorColumns}
                bordered
              />
            </Stack>
          )}

          {summary && (
            <Stack gap="sm">
              <Flex gap="lg" style={{ flexWrap: 'wrap' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.colors.semantic.success.default }}>
                    {summary.nodes_created}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>Nodes Created</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.colors.semantic.info.default }}>
                    {summary.nodes_updated}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>Nodes Updated</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.colors.semantic.success.default }}>
                    {summary.edges_created}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>Edges Created</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.colors.semantic.info.default }}>
                    {summary.edges_updated}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>Edges Updated</div>
                </div>
              </Flex>
              <div style={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>
                Sheets detected: <code>{summary.sheets_detected.nodes}</code>, <code>{summary.sheets_detected.edges}</code>
              </div>
            </Stack>
          )}
        </Stack>
      </Card.Body>
    </Card>
  );
};
